import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { useInvoicePilot } from "./InvoicePilotLink";
import { useCorrections,useCorrectionStatus } from './Corrections';
import { Button, Card, ErrorState, Money, PageHeader, Spinner } from "../components/ui";
import { formatDate } from "../lib/format";
import type { Account, ExpenseListRow } from "../lib/models";

type Detail = ExpenseListRow & { lines: { id: string; accountId: string; description: string | null; amount: number }[] };

export default function ExpenseDetail() {
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const pilot = useInvoicePilot();
  const corrections=useCorrections(),statuses=useCorrectionStatus();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const enabled = !!currentOrg && !!id && pilot.data?.enabled === true;
  const detail = useQuery({queryKey:["expense",currentOrg?.id,id],queryFn:()=>api.get<Detail>(`/expenses/${id}`),enabled});
  const accounts = useQuery({queryKey:["accounts",currentOrg?.id],queryFn:()=>api.get<Account[]>("/accounts"),enabled});
  const currency = currentOrg?.baseCurrency ?? "USD";
  const accountName = (accountId: string) => accounts.data?.find(a => a.id === accountId)?.name ?? "Account unavailable";
  if (pilot.isLoading || (enabled && detail.isLoading)) return <Spinner />;
  if (!enabled) return <p>Expense details are not enabled for this company. <Button onClick={()=>navigate('/expenses')}>All expenses</Button></p>;
  if (detail.error) return <ErrorState error={detail.error}/>;
  if (!detail.data) return null;
  const expense = detail.data;
  return <div>
    <PageHeader title={`Expense ${expense.docNumber ?? ''}`} subtitle={`Recorded ${formatDate(expense.txnDate)}`}/>
    {statuses.data?.reversed.some(x=>x.target_id===expense.id)?<p role="status">This expense was reversed. The original is retained for audit history.</p>:corrections.data?.enabled&&<Button variant="secondary" onClick={()=>navigate(`/corrections?action=reverse_expense&target=${encodeURIComponent(expense.id)}`)}>Correct this expense</Button>}
    {params.get('saved') === '1' && <section className="mb-4 rounded-lg border border-brand-100 bg-brand-50 p-4">
      <p role="status">Expense recorded: <Money cents={expense.total} currency={currency}/>{expense.payeeName ? ` to ${expense.payeeName}` : ''}. No money was sent by this action.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={()=>setParams({}, {replace:true})}>View expense</Button>
        <Button variant="secondary" onClick={()=>navigate('/expenses/new')}>Enter another expense</Button>
        <Button variant="secondary" onClick={()=>navigate('/expenses')}>All expenses</Button>
      </div>
    </section>}
    <Card className="p-6">
      <p>Payee: {expense.payeeName ?? 'Not specified'}</p>
      <p>Payment account: {accounts.isLoading ? 'Loading…' : accountName(expense.paymentAccountId)}</p>
      {accounts.error && <ErrorState error={accounts.error}/>}
      <div className="mt-4 overflow-x-auto"><table className="min-w-full">
        <thead><tr><th className="th">Category</th><th className="th">Description</th><th className="th text-right">Amount</th></tr></thead>
        <tbody>{expense.lines.map(line=><tr key={line.id}><td className="td">{accountName(line.accountId)}</td><td className="td">{line.description ?? '—'}</td><td className="td text-right"><Money cents={line.amount} currency={currency}/></td></tr>)}</tbody>
      </table></div>
      <p className="mt-4 text-right font-semibold">Total: <Money cents={expense.total} currency={currency}/></p>
      {expense.memo && <p className="mt-4">Memo: {expense.memo}</p>}
    </Card>
    {params.get('saved') !== '1' && <div className="mt-4 flex gap-2"><Button onClick={()=>navigate('/expenses/new')}>Enter another expense</Button><Button variant="secondary" onClick={()=>navigate('/expenses')}>All expenses</Button></div>}
  </div>;
}
